/**
 * Closed checks an author can bind onto `disabled`, `error`, and
 * `errorMessage`. These stay off the visibility `when` operator set:
 * a failed visibility predicate unmounts the node, and a field check
 * has to leave the control mounted.
 */

export const FIELD_CHECK_OPERATORS = ['MIN_LENGTH', 'IS_EMAIL'] as const;

export type FieldCheckOperator = (typeof FIELD_CHECK_OPERATORS)[number];

/** Local part, `@`, domain, and a dot. `name@domain` does not match. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isFieldCheckOperator(
  value: unknown,
): value is FieldCheckOperator {
  return (
    typeof value === 'string' &&
    (FIELD_CHECK_OPERATORS as readonly string[]).includes(value)
  );
}

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

export interface FieldCheckSpec {
  readonly operator?: unknown;
  readonly value?: unknown;
  readonly invert?: unknown;
  readonly suppressWhenEmpty?: unknown;
  readonly message?: unknown;
}

/**
 * Boolean targets stay booleans. `errorMessage` stays a string, including
 * when a check omits `message`, so the input never receives `true`.
 */
export function coerceFieldCheckResult(
  propName: string,
  checked: boolean | string,
): boolean | string {
  if (propName === 'errorMessage') {
    return typeof checked === 'string' ? checked : '';
  }
  if (propName === 'disabled' || propName === 'error') {
    return typeof checked === 'string' ? checked.trim().length > 0 : checked;
  }
  return checked;
}

function meetsMinLength(text: string, value: unknown): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    text.length >= value
  );
}

/**
 * Resolve a field-check bind.
 *
 * Returns a boolean, or `message` / `''` when `message` is a string.
 * An unknown operator returns `undefined` so the caller can leave the
 * prop unset. A blank answer forces the inactive result only when
 * `suppressWhenEmpty` is true, and that override runs after `invert`.
 */
export function evaluateFieldCheck(
  raw: unknown,
  spec: FieldCheckSpec,
): boolean | string | undefined {
  if (!isFieldCheckOperator(spec.operator)) {
    return undefined;
  }
  const text = raw == null ? '' : String(raw);
  const matched =
    spec.operator === 'MIN_LENGTH'
      ? meetsMinLength(text, spec.value)
      : isValidEmail(text);
  let active = spec.invert === true ? !matched : matched;
  if (spec.suppressWhenEmpty === true && text.trim().length === 0) {
    active = false;
  }
  if (typeof spec.message === 'string') {
    return active ? spec.message : '';
  }
  return active;
}
